import type { ConflictResolution } from '../../lib/graph/saveBack';

const FONT = "var(--xp-font-body)";

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
};

const dialogStyle: React.CSSProperties = {
  width: 460,
  background: '#ece9d8',
  borderStyle: 'solid',
  borderWidth: 2,
  boxShadow: '3px 3px 0 #000',
  fontFamily: FONT,
  fontSize: 12,
};

const titleBarStyle: React.CSSProperties = {
  background: 'linear-gradient(90deg, #800000, #c04040)',
  color: 'white',
  padding: '3px 6px',
  fontWeight: 'bold',
};

const bodyStyle: React.CSSProperties = {
  padding: 14,
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 6,
  padding: '8px 12px',
  borderTop: '1px solid #808080',
};

function Btn({ label, onClick, primary }: { label: string; onClick: () => void; primary?: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{
        minWidth: 100,
        height: 26,
        padding: '0 12px',
        background: '#ece9d8',
        borderStyle: 'solid',
        borderWidth: 2,
        fontFamily: FONT,
        fontSize: 12,
        fontWeight: primary ? 'bold' : 'normal',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

interface ConflictDialogProps {
  open: boolean;
  documentTitle: string;
  /** Identity of the user (or last writer) of the remote version. Optional. */
  remoteAuthor?: string;
  onResolve: (resolution: ConflictResolution) => void;
  onCancel: () => void;
}

/**
 * Surfaced when save-back hits 412/409: someone edited this SharePoint item
 * while Repancy held a stale etag. Three choices, no diff editor here — that's
 * a future enhancement; for v1 we hand the user a clear three-way decision.
 */
export function ConflictDialog({ open, documentTitle, remoteAuthor, onResolve, onCancel }: ConflictDialogProps) {
  if (!open) return null;
  return (
    <div style={overlayStyle} onClick={onCancel} data-testid="conflict-dialog-overlay">
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()} data-testid="conflict-dialog">
        <div style={titleBarStyle}>⚠ Save Conflict</div>
        <div style={bodyStyle}>
          <p style={{ marginTop: 0 }}>
            <strong>{documentTitle}</strong> was changed in SharePoint
            {remoteAuthor ? <> by <strong>{remoteAuthor}</strong></> : null} while
            you were editing it here.
          </p>
          <p>Pick how to resolve:</p>
          <ul style={{ paddingLeft: 18, lineHeight: 1.5 }}>
            <li><strong>Overwrite:</strong> push your version, replacing theirs.</li>
            <li><strong>Keep theirs:</strong> discard your local edits and pull the remote version.</li>
            <li><strong>Save as new local copy:</strong> keep their remote unchanged; save your edits as a brand-new local document linked to the same matter.</li>
          </ul>
        </div>
        <div style={footerStyle}>
          <Btn label="Overwrite" onClick={() => onResolve('overwrite')} />
          <Btn label="Keep theirs" onClick={() => onResolve('keep-theirs')} />
          <Btn label="Save as new local copy" onClick={() => onResolve('save-as-new-local')} primary />
        </div>
      </div>
    </div>
  );
}
